/**
 * @file MeetingMindmapEvidence.tsx
 * @brief The evidence card a selected mindmap node opens — verbatim quote,
 *        clock and clip playback, or an explicit unanchored state.
 *
 * @details
 * Sits **below** the map / list rather than floating over the canvas: an
 * overlay would cover the branch you just clicked, which is the one thing you
 * are still reading. It is shared by both views (04-08-2026), so selecting a
 * leaf in the list opens the same card the canvas does.
 *
 * The five states are the feature. Four of them are ways of saying "we do not
 * have this", and they are written to be unmistakable, because the failure
 * this whole anchor system exists to prevent is a plausible-looking quote
 * attached to the wrong moment:
 *   - nothing selected      -> a hint, no claim
 *   - topic / root node     -> "a heading, not a spoken moment"
 *   - no confident match    -> "no evidence linked", nothing playable
 *   - matched, unanchored   -> the item's text, stated as NOT located in audio
 *   - matched + anchored    -> the verbatim quote, its clock, and a play button
 * A quote is only ever rendered when it came from the stored anchor. Where the
 * anchor located a span but stored no quote, the card shows the item's own text
 * under a different label ("Moment") — restating an item is honest, presenting
 * that restatement inside quotation marks would not be.
 *
 * @dependencies
 * - ./mindmapEvidence: node -> item -> anchor resolution
 * - ./meetingEvidence: anchorLabel / anchorClipSecs (shared with the tabs)
 * - components/ui/AudioPlayer: AudioPlayBadge, the same clip player the
 *   Decisions / Key points rows use
 *
 * @references
 * - docs/product/meeting-intel-evidence-anchor-plan.md
 * - features/company-meetings/components/MeetingDetailView.tsx (`MindmapTab`)
 *
 * @author DES
 * @date 04-08-2026
 */

import type { ReactNode } from "react";
import { CircleSlash, Info, Quote, Volume1, X } from "lucide-react";
import { AudioPlayBadge } from "@/components/ui/AudioPlayer";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";
import { anchorClipSecs, anchorLabel } from "./meetingEvidence";
import type { MindmapEvidence } from "./mindmapEvidence";
import { mindmapVisual, type MindmapNodeData } from "./mindmapGraph";

const KIND_LABEL: Record<string, string> = {
  decision: "Decision",
  action: "Action item",
  risk: "Risk",
  point: "Key point",
};

// ----------------------------------------------------------------------
// Shell — one frame for all five states so the tab does not jump height
// when the selection moves between an anchored and an unanchored node.
// ----------------------------------------------------------------------
function Shell({
  children,
  onClose,
  bare = false,
}: {
  children: ReactNode;
  onClose?: () => void;
  /** No frame and no ✕: the phone tree shows it inline (DES 30-09-2026). */
  bare?: boolean;
}) {
  if (bare) return <div>{children}</div>;
  return (
    <div className="relative rounded-xl border border-border bg-card p-4">
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Clear selection"
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
      {children}
    </div>
  );
}

function Hint({ icon: Icon, text }: { icon: typeof Info; text: string }) {
  return (
    <p className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {text}
    </p>
  );
}

export function MeetingMindmapEvidence({
  node,
  evidence,
  recordingUrl,
  onClose,
  bare = false,
}: {
  node: MindmapNodeData | null;
  evidence: MindmapEvidence | null;
  recordingUrl: string | null;
  onClose: () => void;
  /** Inline, without the card: no frame, no header (the row above already
   *  names the node) and no ✕ (clicking the row again closes it). Used by
   *  the phone-style tree; the canvas keeps the card. (30-09-2026) */
  bare?: boolean;
}) {
  if (!node) {
    return (
      <Shell>
        <Hint
          icon={Info}
          text="Select a node to see the moment in the meeting it came from."
        />
      </Shell>
    );
  }

  const { Icon, color } = mindmapVisual(node.type);
  const label = node.label ?? "Untitled";
  const isStructural = !node.type || node.type === "root" || node.type === "topic";

  const header = (
    <div className="flex items-start gap-2.5 pr-8">
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `${color}1A` }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color }} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          dir={isRTLText(label) ? "rtl" : undefined}
          className={cn(
            "block text-sm font-medium",
            isRTLText(label) && "text-right",
          )}
        >
          {label}
        </span>
        {/* Caption follows the label's direction — a right-aligned Urdu title
            over a left-aligned caption reads as two unrelated lines. */}
        <span
          className={cn(
            "mt-0.5 block text-[11px] capitalize text-muted-foreground",
            isRTLText(label) && "text-right",
          )}
        >
          {node.type ?? "node"}
        </span>
      </span>
    </div>
  );

  // A topic is a grouping the model invented to hold things; it was never said
  // out loud, so there is nothing to play and nothing to quote.
  if (isStructural) {
    return (
      <Shell onClose={onClose} bare={bare}>
        {bare ? null : header}
        <div className={bare ? "" : "mt-3 border-t border-border/60 pt-3"}>
          <Hint
            icon={Info}
            text="A heading, not a spoken moment — open one of its decisions, actions or risks to hear the evidence."
          />
        </div>
      </Shell>
    );
  }

  if (!evidence) {
    return (
      <Shell onClose={onClose} bare={bare}>
        {bare ? null : header}
        <div className={bare ? "" : "mt-3 border-t border-border/60 pt-3"}>
          <Hint
            icon={CircleSlash}
            text="No evidence linked to this node — it could not be matched to a decision, action or key point with confidence."
          />
        </div>
      </Shell>
    );
  }

  const anchor = evidence.anchor;
  const canPlay = Boolean(recordingUrl && anchor);
  // Quote only when the anchor stored one; otherwise show the item's own text
  // and label it as a restatement, never as a quotation.
  const excerpt = evidence.quote ?? evidence.itemText;
  const isVerbatim = Boolean(evidence.quote);
  const excerptRtl = isRTLText(excerpt);

  return (
    <Shell onClose={onClose} bare={bare}>
      {bare ? null : header}

      <div className={cn("space-y-2.5", !bare && "mt-3 border-t border-border/60 pt-3")}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
            {isVerbatim ? (
              <Quote className="h-3 w-3" />
            ) : (
              <Info className="h-3 w-3" />
            )}
            {/* "Moment" would be a claim about the recording; only say it when
                the item is actually located in one. */}
            {isVerbatim ? "Said in the meeting" : anchor ? "Moment" : null}
            {isVerbatim || anchor ? (
              <span className="text-muted-foreground/60">·</span>
            ) : null}
            {KIND_LABEL[evidence.kind] ?? evidence.kind}
          </span>

          {anchor ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-medium text-brand">
              {/* A leading "~" is the entire confidence UI — same convention as
                  the Decisions / Key points rows. */}
              {anchorLabel(anchor)}
            </span>
          ) : null}

          {canPlay && anchor ? (
            <AudioPlayBadge
              src={recordingUrl}
              variant="original"
              startSecs={Math.max(0, anchor.startSecs)}
              clipSecs={anchorClipSecs(anchor)}
              // Volume1, not the filled Play — same reasoning as the Decisions
              // rows: a solid triangle would be the only filled mark in a
              // stroke-only icon set.
              icon={<Volume1 className="h-3.5 w-3.5" />}
              label="Play"
              title={`Play clip at ${anchorLabel(anchor)}`}
              playingClassName="border-brand bg-brand text-brand-foreground hover:bg-brand hover:text-brand-foreground"
            />
          ) : null}
        </div>

        <blockquote
          dir={excerptRtl ? "rtl" : undefined}
          className={cn(
            "border-l-2 border-brand/40 pl-3 text-sm leading-relaxed text-foreground/90",
            excerptRtl && "border-l-0 border-r-2 pl-0 pr-3 text-right",
          )}
        >
          {excerpt}
        </blockquote>

        {/* The node label is a restatement; when the matched item reads
            differently, showing both is what makes the link checkable.
            A Latin "Action item:" prefix in front of RTL text is a bidi trap —
            set `dir="rtl"` on the whole line and the colon jumps to the wrong
            side of the label. The prefix stays LTR and `<bdi>` isolates the
            item text, so each half runs in its own direction. */}
        {isVerbatim && evidence.itemText.trim() !== excerpt.trim() ? (
          <p className="text-xs text-muted-foreground">
            {KIND_LABEL[evidence.kind] ?? evidence.kind}:{" "}
            <bdi>{evidence.itemText}</bdi>
          </p>
        ) : null}

        {!anchor ? (
          <Hint
            icon={CircleSlash}
            text="Not anchored to the recording — this one cannot be played."
          />
        ) : null}
      </div>
    </Shell>
  );
}
